import * as classnames from 'classnames';
import * as React from 'react';

import { FormattedMessage, InjectedIntlProps  } from 'react-intl';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';

import { getUnverifiedEmail } from 'actions/account';
import { Button } from 'components/Button';
import { Initial } from 'components/Initial';
import { LayoutHeader } from 'components/Layout';
import { WalletAccountTypeCard } from 'components/Wallet';
import {
  ROUTE_ACCOUNT_EMAIL_UNVERIFIED,
  ROUTE_ACCOUNT_EMAIL_UPDATE,
  ROUTE_ACCOUNT_KYC_OVERVIEW,
  ROUTE_ACCOUNT_PASSWORD_UPDATE,
  ROUTE_ACCOUNT_PHONE_DETAIL,
  ROUTE_ACCOUNT_STORE_UPDATE
} from 'constants/app';
import { WalletAccountTypeId } from 'constants/wallet';
import { ImageProfileSampleKtp } from 'images/index';
import { Account } from 'models/account';
import { AppState } from 'models/app';
import { WalletAccount } from 'models/wallet';

import './AccountProfile.scss';

interface MenuProps {
  footnote?: string;
  iconClassName?: string;
  info?: string;
  isError?: boolean;
  label: string;
  redirectTo: string;
  value?: string;
}

interface StateProps {
  account: Account;
  walletAccount: WalletAccount;
}

interface DispatchProps {
  getUnverifiedEmail: typeof getUnverifiedEmail;
}

type OwnProps = InjectedIntlProps & RouteComponentProps<{}>;
type AccountProfileProps = OwnProps & StateProps & DispatchProps;

class AccountProfileContainer extends React.Component<AccountProfileProps, {}> {
  constructor(props: AccountProfileProps) {
    super(props);

    this.profileMenu = this.profileMenu.bind(this);
    this.redirectTo = this.redirectTo.bind(this);
  }

  componentWillMount() {
    const { account } = this.props;
    if (!account.email) {
      this.props.getUnverifiedEmail();
    }
  }

  render() {
    const { history } = this.props;

    return (
      <div className="o-container">
        <LayoutHeader actionIcon="ic-arrow-left" actionHandler={history.goBack}>
          <h1 className="o-layout-header-title">
            <FormattedMessage id="account.profile.title"/>
          </h1>
        </LayoutHeader>

        {this.profileDetails}

        <div className="account-profile__menus">
          {this.phoneDetailMenu}
          {this.emailUpdateMenu}
          {this.passwordUpdateMenu}
          {this.storeUpdateMenu}
        </div>
      </div>
    );
  }

  get profileDetails() {
    const { account, walletAccount } = this.props;

    if (walletAccount.type.id === WalletAccountTypeId.PRO && account.profile) {
      const initialMaxLetter = 1;
      return (
        <div className="account-profile__top-container o-container__flex-row">
          <Initial className="account-profile__initial" maxLetter={initialMaxLetter} name={account.profile.name}/>
          <div className="account-profile__name-and-city-container o-container__flex-column o-container__flex-vertical--center o-container__flex--full">
            <div className="u-medium">
              {account.profile.name}
            </div>
            <WalletAccountTypeCard className="account-profile__wallet-account-type" walletAccountType={walletAccount.type} />
          </div>
        </div>
      );
    }

    if (walletAccount.type.id === WalletAccountTypeId.LITE) {
      return (
        <div className="account-profile__get-kaspay-pro-container o-container o-container--fluid o-container--fluid-vertical o-container__flex-column">
          <img className="account-profile__get-kaspay-pro-image" alt="profile sample ktp" src={ImageProfileSampleKtp}/>
          <span className="o-subhead u-medium">
            <FormattedMessage id="account.profile.kaspay.pro" />
          </span>
          <p className="account-profile__get-kaspay-pro-text">
            <FormattedMessage id="account.profile.get.kaspay.pro" />
          </p>
          <Button
            className="button--primary button--full"
            type="button"
            onClick={this.redirectTo(ROUTE_ACCOUNT_KYC_OVERVIEW)}
          >
            <FormattedMessage id="account.profile.see.more.button"/>
          </Button>
        </div>
      );
    }

    return null;
  }

  get phoneDetailMenu() {
    const props: MenuProps = {
      label: 'account.profile.phonenumber',
      redirectTo: ROUTE_ACCOUNT_PHONE_DETAIL,
      value: this.props.account.phone
    };

    return this.profileMenu(props);
  }

  get emailUpdateMenu() {
    const { email, unverifiedEmail } = this.props.account;

    if (!email && unverifiedEmail) {
      const unverifiedProps: MenuProps = {
        footnote: 'account.profile.email.verify.footnote',
        info: 'account.profile.email.verify',
        isError: true,
        label: 'account.profile.email',
        redirectTo: ROUTE_ACCOUNT_EMAIL_UNVERIFIED,
        value: unverifiedEmail
      };

      return this.profileMenu(unverifiedProps);
    }

    const props: MenuProps = {
      iconClassName: 'ic-edit',
      label: 'account.profile.email',
      redirectTo: ROUTE_ACCOUNT_EMAIL_UPDATE,
      value: email
    };

    return this.profileMenu(props);
  }

  get passwordUpdateMenu() {
    const props: MenuProps = {
      iconClassName: 'ic-edit',
      label: 'account.profile.password',
      redirectTo: ROUTE_ACCOUNT_PASSWORD_UPDATE
    };

    return this.profileMenu(props);
  }

  get storeUpdateMenu() {
    const { store } = this.props.account;

    if (store) {
      const editProps: MenuProps = {
        iconClassName: 'ic-edit',
        label: 'account.profile.store',
        redirectTo: ROUTE_ACCOUNT_STORE_UPDATE,
        value: store.storeName
      };

      return this.profileMenu(editProps);
    }

    const props: MenuProps = {
      iconClassName: 'account-profile__menu-action-icon--blue ic-plus-circle',
      label: 'account.profile.store',
      redirectTo: ROUTE_ACCOUNT_STORE_UPDATE
    };

    return this.profileMenu(props);
  }

  private profileMenu(props: MenuProps) {
    const { footnote, iconClassName, info, isError, label, redirectTo, value } = props;
    const labelClassNameModifier = !value && 'account-profile__menu-label--large';
    const footnoteModifier = isError && 'account-profile__menu-footnote--error';

    return (
      <div className="account-profile__menu-container o-container__flex-row" onClick={this.redirectTo(redirectTo)}>
        <div className="o-container__flex--full">
          <div className={classnames('account-profile__menu-label o-footnote-1', labelClassNameModifier)}>
            <FormattedMessage id={label}/>
          </div>
          {value && <div className="account-profile__menu-value">{value}</div>}
          {footnote && <span className={classnames(footnoteModifier, 'o-footnote-1')}><FormattedMessage id={footnote}/></span>}
        </div>
        <div className="o-container__flex-row o-container__flex--center">
          {iconClassName && <i className={classnames(iconClassName, 'account-profile__menu-action-icon o-icon-action')}/>}
          {info && <span className="account-profile__menu-info o-caption-2"><FormattedMessage id={info}/></span>}
        </div>
      </div>
    );
  }

  private redirectTo(redirectPath: string) {
    return () => {
      const { history } = this.props;
      history.push(redirectPath);
    };
  }
}

export const AccountProfile = connect<StateProps, {}, OwnProps>(
  (state: AppState) => ({
    account: state.accountState.account,
    walletAccount: state.walletState.accountState.walletAccount
  }), { getUnverifiedEmail }
)(AccountProfileContainer);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Account/Profile/AccountProfile/AccountProfile.tsx